import { signupSource } from "@/lib/referral";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PacManBar from "@/pages/worth/PacManBar";

/** Processes the `session_id` fragment that Emergent OAuth drops into the URL.
 *
 *  Mount-only effect with a ref-based "already running" guard so React 18
 *  StrictMode's double-invoke doesn't fire two exchanges (the server-side
 *  single-use token would reject the second). After a successful exchange we
 *  stuff the user into AuthContext and bounce to "/" — the normal route
 *  tree then renders the Create page.
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 *  THIS BREAKS THE AUTH. */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Parse session_id from URL fragment (not query string).
    const hash = (location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("No session id in URL — try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId, source: signupSource() });
        // Clean the hash so refreshing doesn't retry a now-consumed token.
        try { window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
        setUser(data);
        setIsAuthenticated(true);
        toast.success(`Welcome${data?.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
        navigate("/", { replace: true, state: { user: data } });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
        setError(detail);
        // Give the user a chance to read, then re-pull /auth/me in case the
        // cookie set but the response body missed. Avoids silent bounce.
        setTimeout(() => { checkAuth(); }, 2000);
      }
    })();
  }, [location, navigate, setUser, setIsAuthenticated, checkAuth]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8FAFC] px-4 text-center" data-testid="auth-callback">
      {error ? (
        <div className="max-w-sm rounded-lg border border-rose-200 bg-rose-50 p-6">
          <p className="text-sm font-semibold text-rose-900">Couldn't sign you in</p>
          <p className="mt-1 text-xs text-rose-700" data-testid="auth-callback-error">{error}</p>
          <button
            type="button"
            onClick={() => { window.location.href = "/"; }}
            className="mt-4 rounded-md bg-white px-3 py-2 text-xs font-semibold text-slate-900 shadow-sm hover:bg-slate-50"
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <PacManBar className="" label="Signing you in…" testid="auth-callback-bar" />
      )}
    </div>
  );
}
